import axios from 'axios'

import { IApplicationSetting } from '@src/areas/app/models/IApplicationSetting'
import { IResetPasswordForm } from '@src/areas/auth/containers/ResetPassword'
import { ISignInForm } from '@src/areas/auth/containers/SignIn'
import { ISignUpForm } from '@src/areas/auth/containers/SignUp'
import { authParseTariff } from '@src/areas/auth/utils/parseTariff'
import { IChangePasswordForm } from '@src/components/Layout/Header/InfoBlock/components/Profile/components/ChangePasswordDialog/Form'
import {
    AppInitialStateResponse,
    ChangeUserPasswordRequest,
    GetTenantRegistrationFormValuesResponse,
    RegisterTenantRequest,
    ResetUserPasswordConfirmRequest,
    ResetUserPasswordRequest,
    TokenSignInRequest,
    UserSignOutRequest
} from '@src/models/api'

export type IAppInitialState = Replace<
    AppInitialStateResponse,
    {
        appSettingsInitialState?: IApplicationSetting
    }
>

export const authTokenName = 'appToken'
export const apiTokenName = 'sptoken'

const authStorageService = {
    getLocalStorageToken: () => localStorage.getItem(authTokenName),
    getCommonToken: () => {
        const sessionToken = sessionStorage.getItem(authTokenName)
        const localToken = authStorageService.getLocalStorageToken()

        return sessionToken && localToken ? sessionToken : localToken
    },
    removeLocalStorageToken: () => {
        localStorage.removeItem(authTokenName)
        sessionStorage.removeItem(authTokenName)
    },
    setLocalStorageToken: (token: string) => {
        localStorage.setItem(authTokenName, token)
        sessionStorage.setItem(authTokenName, token)
    }
}

export class AuthApiService {
    static getRegistrationFormValues = () => {
        return axios.get<GetTenantRegistrationFormValuesResponse>('/api/account/tenant-registration-form-values')
    }

    static register = (values: ISignUpForm) => {
        const requestPayload: RegisterTenantRequest = {
            ...values,
            ...authParseTariff(values.tariff)
        }

        return axios.post<void>('/api/account/register-tenant', requestPayload)
    }

    static signIn = async (values: ISignInForm) => {
        const { data } = await axios.post<AppInitialStateResponse>('/api/account/user-sign-in', values)

        const token = data.appToken

        if (values.remember) {
            authStorageService.setLocalStorageToken(data.appToken)
        }

        axios.defaults.headers[apiTokenName] = token

        return data
    }

    static signOut = async (removeSessionstorageToken: boolean) => {
        const appToken = axios.defaults.headers[apiTokenName]

        const requestPayload: UserSignOutRequest = { appToken }

        const { data } = await axios.post<void>('/api/account/user-sign-out', requestPayload)

        localStorage.removeItem(authTokenName)
        if (removeSessionstorageToken) {
            sessionStorage.removeItem(authTokenName)
        }

        return data
    }

    static tenantTokenSignIn = async (updateToken = false) => {
        const token = authStorageService.getCommonToken()

        const requestPayload: TokenSignInRequest = {
            token
        }

        if (token) {
            const { data } = await axios.post<AppInitialStateResponse>(
                '/api/account/tenant-token-sign-in',
                requestPayload
            )

            const token = data.appToken

            if (updateToken) {
                authStorageService.setLocalStorageToken(token)
                axios.defaults.headers[apiTokenName] = token
            }

            return data
        } else {
            authStorageService.removeLocalStorageToken()
            throw Error()
        }
    }

    static pwdlessTokenSignIn = async (token: string) => {
        const requestPayload: TokenSignInRequest = {
            token
        }

        const { data } = await axios.post<AppInitialStateResponse>('/api/account/pwdless-token-sign-in', requestPayload)

        axios.defaults.headers[apiTokenName] = data.appToken

        return data
    }

    static switchTenant = async (tenantToken: string) => {
        const requestPayload: TokenSignInRequest = {
            token: axios.defaults.headers[apiTokenName]
        }

        try {
            const { data } = await axios.post<AppInitialStateResponse>(
                '/api/account/tenant-token-sign-in',
                requestPayload,
                {
                    params: {
                        tenant: tenantToken
                    }
                }
            )

            const token = data.appToken
            axios.defaults.headers[apiTokenName] = token

            /* Если сохранили в localStorage, значит был флаг remember при логине */
            if (authStorageService.getLocalStorageToken()) {
                authStorageService.setLocalStorageToken(token)
            }

            return data
        } catch (err) {
            authStorageService.removeLocalStorageToken()
        }
    }

    static resetPassword = async (email: string) => {
        const requestPayload: ResetUserPasswordRequest = {
            email
        }

        await axios.post<void>('/api/account/reset-password', requestPayload)
    }

    static resetPasswordConfirm = async ({ email, resetCode, newPassword }: IResetPasswordForm) => {
        const requestPayload: ResetUserPasswordConfirmRequest = {
            resetCode,
            newPassword
        }

        return await axios.post<void>('/api/account/reset-password-confirm', {
            email,
            ...requestPayload
        })
    }

    static changePassword = async (values: IChangePasswordForm) => {
        const requestPayload: ChangeUserPasswordRequest = {
            ...values,
            appToken: axios.defaults.headers[apiTokenName]
        }

        const { data } = await axios.post<void>('/api/account/change-password', requestPayload)

        return data
    }
}
